import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useShop } from '../App';
import CustomerModal from '../components/CustomerModal';
import TransactionModal from '../components/TransactionModal';
import RepairModal from '../components/RepairModal';
import CustomJobModal from '../components/CustomJobModal';
import AppraisalModal from '../components/AppraisalModal';
import MessageChat from '../components/MessageChat';
import AddRelationshipModal from '../components/AddRelationshipModal';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ToastContainer';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function CustomerDashboard() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { shops } = useShop();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  // Summary stats state
  const [customerSummary, setCustomerSummary] = useState(null);
  const [summaryQuickRange, setSummaryQuickRange] = useState('all-time');
  const [summaryDateFrom, setSummaryDateFrom] = useState('2000-01-01');
  const [summaryDateTo, setSummaryDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Transactions tab state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const { toasts, success, error: toastError } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTransaction, setEditingTransaction] = useState(null);
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filterLayaway, setFilterLayaway] = useState('any');
  const [filterClosed, setFilterClosed] = useState('any');
  const [filterBalance, setFilterBalance] = useState('any');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [quickRange, setQuickRange] = useState('');
  const [filterShopId, setFilterShopId] = useState('');

  // Helper: format date as YYYY-MM-DD
  const formatDate = (d) => d.toISOString().split('T')[0];
  const today = new Date();

  // Quick date range handlers
  const setThisWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    setDateFrom(formatDate(sundayThisWeek));
    setDateTo(formatDate(today));
  };
  const setLastWeek = () => {
    const sundayThisWeek = new Date(today);
    sundayThisWeek.setDate(today.getDate() - today.getDay());
    const sundayLastWeek = new Date(sundayThisWeek);
    sundayLastWeek.setDate(sundayThisWeek.getDate() - 7);
    setDateFrom(formatDate(sundayLastWeek));
    setDateTo(formatDate(sundayThisWeek));
  };
  const setThisMonth = () => {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    setDateFrom(formatDate(monthStart));
    setDateTo(formatDate(today));
  };
  const setLastMonth = () => {
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    setDateFrom(formatDate(lastMonthStart));
    setDateTo(formatDate(thisMonthStart));
  };
  const setThisQuarter = () => {
    const quarter = Math.floor(today.getMonth() / 3);
    const quarterStart = new Date(today.getFullYear(), quarter * 3, 1);
    setDateFrom(formatDate(quarterStart));
    setDateTo(formatDate(today));
  };
  const setLastQuarter = () => {
    const currentQuarter = Math.floor(today.getMonth() / 3);
    const thisQuarterStart = new Date(today.getFullYear(), currentQuarter * 3, 1);
    const lastQuarterStart = new Date(today.getFullYear(), (currentQuarter - 1) * 3, 1);
    setDateFrom(formatDate(lastQuarterStart));
    setDateTo(formatDate(thisQuarterStart));
  };
  const setThisYear = () => {
    const yearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDate(yearStart));
    setDateTo(formatDate(today));
  };
  const setLastYear = () => {
    const lastYear = today.getFullYear() - 1;
    const lastYearStart = new Date(lastYear, 0, 1);
    const thisYearStart = new Date(today.getFullYear(), 0, 1);
    setDateFrom(formatDate(lastYearStart));
    setDateTo(formatDate(thisYearStart));
  };
  const setAllTime = () => {
    setDateFrom('2000-01-01');
    setDateTo(formatDate(today));
  };

  const handleQuickRangeChange = (e) => {
    const range = e.target.value;
    setQuickRange(range);
    const rangeHandlers = {
      'this-week': setThisWeek, 'last-week': setLastWeek,
      'this-month': setThisMonth, 'last-month': setLastMonth,
      'this-quarter': setThisQuarter, 'last-quarter': setLastQuarter,
      'this-year': setThisYear, 'last-year': setLastYear,
      'all-time': setAllTime,
    };
    if (rangeHandlers[range]) {
      rangeHandlers[range]();
    } else {
      setDateFrom('');
      setDateTo('');
    }
  };

  const resetFilters = () => {
    setFilterLayaway('any');
    setFilterClosed('any');
    setFilterBalance('any');
    setDateFrom('');
    setDateTo('');
    setQuickRange('');
    setFilterShopId('');
    setShowFilters(false);
  };

  const toggleFilters = () => {
    if (showFilters) {
      resetFilters();
    } else {
      setShowFilters(true);
    }
  };
  // Repairs tab state
  const [repairs, setRepairs] = useState([]);
  const [repairsLoading, setRepairsLoading] = useState(false);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [editingRepair, setEditingRepair] = useState(null);
  const [repairSearchQuery, setRepairSearchQuery] = useState('');
  const [repairShowFilters, setRepairShowFilters] = useState(false);
  const [repairFilterStatus, setRepairFilterStatus] = useState('any');
  const [repairFilterJewelryType, setRepairFilterJewelryType] = useState('any');
  const [repairFilterDueType, setRepairFilterDueType] = useState('any');
  const [repairFilterShopId, setRepairFilterShopId] = useState('');
  const [repairDateFrom, setRepairDateFrom] = useState('');
  const [repairDateTo, setRepairDateTo] = useState('');
  const [repairQuickRange, setRepairQuickRange] = useState('');

  // Custom Jobs tab state
  const [customJobs, setCustomJobs] = useState([]);
  const [customJobsLoading, setCustomJobsLoading] = useState(false);
  const [showCustomJobModal, setShowCustomJobModal] = useState(false);
  const [editingCustomJob, setEditingCustomJob] = useState(null);
  const [customJobSearchQuery, setCustomJobSearchQuery] = useState('');
  const [customJobShowFilters, setCustomJobShowFilters] = useState(false);
  const [customJobFilterStatus, setCustomJobFilterStatus] = useState('any');
  const [customJobFilterJewelryType, setCustomJobFilterJewelryType] = useState('any');
  const [customJobFilterDueType, setCustomJobFilterDueType] = useState('any');
  const [customJobFilterShopId, setCustomJobFilterShopId] = useState('');
  const [customJobDateFrom, setCustomJobDateFrom] = useState('');
  const [customJobDateTo, setCustomJobDateTo] = useState('');
  const [customJobQuickRange, setCustomJobQuickRange] = useState('');

  const customJobResetFilters = () => {
    setCustomJobFilterStatus('any');
    setCustomJobFilterJewelryType('any');
    setCustomJobFilterDueType('any');
    setCustomJobFilterShopId('');
    setCustomJobDateFrom('');
    setCustomJobDateTo('');
    setCustomJobQuickRange('');
    setCustomJobShowFilters(false);
  };

  const customJobToggleFilters = () => {
    if (customJobShowFilters) {
      customJobResetFilters();
    } else {
      setCustomJobShowFilters(true);
    }
  };

  const repairResetFilters = () => {
    setRepairFilterStatus('any');
    setRepairFilterJewelryType('any');
    setRepairFilterDueType('any');
    setRepairFilterShopId('');
    setRepairDateFrom('');
    setRepairDateTo('');
    setRepairQuickRange('');
    setRepairShowFilters(false);
  };

  const repairToggleFilters = () => {
    if (repairShowFilters) {
      repairResetFilters();
    } else {
      setRepairShowFilters(true);
    }
  };
  // Appraisals tab state
  const [appraisals, setAppraisals] = useState([]);
  const [appraisalsLoading, setAppraisalsLoading] = useState(false);
  const [showAppraisalModal, setShowAppraisalModal] = useState(false);
  const [editingAppraisal, setEditingAppraisal] = useState(null);
  const [appraisalSearchQuery, setAppraisalSearchQuery] = useState('');

  // Store Credits tab state
  const [storeCredits, setStoreCredits] = useState([]);
  const [storeCreditsLoading, setStoreCreditsLoading] = useState(false);
  const [storeCreditsBalance, setStoreCreditsBalance] = useState(0);
  const [showAddCreditForm, setShowAddCreditForm] = useState(false);
  const [editingCredit, setEditingCredit] = useState(null);
  const [newCreditAmount, setNewCreditAmount] = useState('');
  const [newCreditNotes, setNewCreditNotes] = useState('');
  const [newCreditDate, setNewCreditDate] = useState(new Date().toISOString().split('T')[0]);

  // Messages tab state
  const [customerConversation, setCustomerConversation] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Friends & Family tab state
  const [relationships, setRelationships] = useState([]);
  const [relationshipsLoading, setRelationshipsLoading] = useState(false);
  const [showAddRelationshipModal, setShowAddRelationshipModal] = useState(false);
  const [editingRelationship, setEditingRelationship] = useState(null);

  // Log tab state
  const [customerLogs, setCustomerLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logEventType, setLogEventType] = useState('visit');
  const [logNote, setLogNote] = useState('');
  const [logTimestamp, setLogTimestamp] = useState(new Date().toISOString().slice(0, 16));
  const [logDateFrom, setLogDateFrom] = useState('');
  const [logDateTo, setLogDateTo] = useState('');
  const [logQuickRange, setLogQuickRange] = useState('');

  useEffect(() => {
    fetchCustomer();
    fetchCustomerSummary();
    fetchStoreCreditsBalance();
  }, [customerId]);

  // Fetch customer summary stats
  const fetchCustomerSummary = async () => {
    try {
      const params = new URLSearchParams();
      params.set('date_from', summaryDateFrom);
      params.set('date_to', summaryDateTo);
      const res = await fetch(`${API_URL}/customers/${customerId}/summary?${params.toString()}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCustomerSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch customer summary:', err);
    }
  };

  // Re-fetch summary when date range changes
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchCustomerSummary();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [summaryDateFrom, summaryDateTo]);

  // Summary quick range handler
  const handleSummaryQuickRangeChange = (e) => {
    const range = e.target.value;
    setSummaryQuickRange(range);
    if (range === 'all-time') {
      setSummaryDateFrom('2000-01-01');
      setSummaryDateTo(new Date().toISOString().split('T')[0]);
    } else if (range === 'this-year') {
      setSummaryDateFrom(`${new Date().getFullYear()}-01-01`);
      setSummaryDateTo(new Date().toISOString().split('T')[0]);
    } else if (range === 'last-year') {
      const lastYear = new Date().getFullYear() - 1;
      setSummaryDateFrom(`${lastYear}-01-01`);
      setSummaryDateTo(`${new Date().getFullYear()}-01-01`);
    } else if (range === 'this-quarter') {
      const quarter = Math.floor(new Date().getMonth() / 3);
      setSummaryDateFrom(`${new Date().getFullYear()}-${String(quarter * 3 + 1).padStart(2, '0')}-01`);
      setSummaryDateTo(new Date().toISOString().split('T')[0]);
    } else if (range === 'last-quarter') {
      const currentQuarter = Math.floor(new Date().getMonth() / 3);
      const lastQuarterStart = (currentQuarter - 1) * 3;
      setSummaryDateFrom(`${new Date().getFullYear()}-${String(lastQuarterStart + 1).padStart(2, '0')}-01`);
      setSummaryDateTo(`${new Date().getFullYear()}-${String(currentQuarter * 3 + 1).padStart(2, '0')}-01`);
    } else if (range === 'this-month') {
      const now = new Date();
      setSummaryDateFrom(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
      setSummaryDateTo(new Date().toISOString().split('T')[0]);
    } else if (range === 'last-month') {
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setSummaryDateFrom(`${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}-01`);
      setSummaryDateTo(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
    } else if (range === 'this-week') {
      const now = new Date();
      const sunday = new Date(now);
      sunday.setDate(now.getDate() - now.getDay());
      setSummaryDateFrom(sunday.toISOString().split('T')[0]);
      setSummaryDateTo(new Date().toISOString().split('T')[0]);
    } else if (range === 'last-week') {
      const now = new Date();
      const sundayThisWeek = new Date(now);
      sundayThisWeek.setDate(now.getDate() - now.getDay());
      const sundayLastWeek = new Date(sundayThisWeek);
      sundayLastWeek.setDate(sundayThisWeek.getDate() - 7);
      setSummaryDateFrom(sundayLastWeek.toISOString().split('T')[0]);
      setSummaryDateTo(sundayThisWeek.toISOString().split('T')[0]);
    }
  };

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  // Fetch transactions for this customer
  // Fetch store credits when tab is active
  useEffect(() => {
    if (activeTab === 'storeCredits') {
      fetchStoreCredits();
      fetchStoreCreditsBalance();
    }
  }, [activeTab, customerId]);

  useEffect(() => {
    if (activeTab === 'repairs') {
      fetchRepairs();
    }
  }, [activeTab, customerId]);

  // Fetch appraisals when tab is active
  useEffect(() => {
    if (activeTab === 'appraisals') {
      fetchAppraisals();
    }
  }, [activeTab, customerId]);

  // Fetch custom jobs when tab is active
  useEffect(() => {
    if (activeTab === 'custom-jobs') {
      fetchCustomJobs();
    }
  }, [activeTab, customerId]);

  // Fetch conversation for this customer when messages tab is active
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchCustomerConversation();
    }
  }, [activeTab, customerId]);

  // Fetch relationships when tab is active
  useEffect(() => {
    if (activeTab === 'friendsAndFamily') {
      fetchRelationships();
    }
  }, [activeTab, customerId]);

  // Fetch logs when tab is active
  useEffect(() => {
    if (activeTab === 'log') {
      fetchCustomerLogs();
    }
  }, [activeTab, customerId]);

  // Debounced log fetch when filters change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (activeTab === 'log') {
        fetchCustomerLogs();
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [logDateFrom, logDateTo]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (activeTab === 'appraisals') {
        fetchAppraisals();
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [appraisalSearchQuery]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (activeTab === 'repairs') {
        fetchRepairs();
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [repairSearchQuery, repairFilterStatus, repairFilterJewelryType, repairFilterDueType, repairFilterShopId, repairDateFrom, repairDateTo]);

  // Debounced custom jobs fetch when filters change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (activeTab === 'custom-jobs') {
        fetchCustomJobs();
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [customJobSearchQuery, customJobFilterStatus, customJobFilterJewelryType, customJobFilterDueType, customJobFilterShopId, customJobDateFrom, customJobDateTo]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (activeTab === 'transactions') {
        fetchTransactions();
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, activeTab, filterLayaway, filterClosed, filterBalance, dateFrom, dateTo, filterShopId]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/customers/${customerId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch customer');
      const data = await res.json();
      setCustomer(data.customer);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerConversation = async () => {
    setMessagesLoading(true);
    try {
      const res = await fetch(`${API_URL}/conversations/by-customer/${customerId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCustomerConversation(data.conversation || null);
      }
    } catch (err) {
      console.error('Failed to fetch conversation:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleConversationStarted = (conversationSid) => {
    // Fetch the conversation details after it's created
    fetchCustomerConversation();
  };

  // Log tab quick range handlers
  const setLogThisWeek = () => {
    const now = new Date();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - now.getDay());
    setLogDateFrom(sunday.toISOString().split('T')[0]);
    setLogDateTo(new Date().toISOString().split('T')[0]);
  };
  const setLogThisMonth = () => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    setLogDateFrom(monthStart.toISOString().split('T')[0]);
    setLogDateTo(new Date().toISOString().split('T')[0]);
  };
  const setLogThisQuarter = () => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const quarterStart = new Date(now.getFullYear(), quarter * 3, 1);
    setLogDateFrom(quarterStart.toISOString().split('T')[0]);
    setLogDateTo(new Date().toISOString().split('T')[0]);
  };
  const setLogThisYear = () => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    setLogDateFrom(yearStart.toISOString().split('T')[0]);
    setLogDateTo(new Date().toISOString().split('T')[0]);
  };
  const setLogAllTime = () => {
    setLogDateFrom('2000-01-01');
    setLogDateTo(new Date().toISOString().split('T')[0]);
  };

  const handleLogQuickRangeChange = (e) => {
    const range = e.target.value;
    setLogQuickRange(range);
    const handlers = {
      'this-week': setLogThisWeek,
      'this-month': setLogThisMonth,
      'this-quarter': setLogThisQuarter,
      'this-year': setLogThisYear,
      'all-time': setLogAllTime,
    };
    if (handlers[range]) {
      handlers[range]();
    } else {
      setLogDateFrom('');
      setLogDateTo('');
    }
  };

  // Fetch customer logs
  const fetchCustomerLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (logDateFrom) params.set('date_from', logDateFrom);
      if (logDateTo) params.set('date_to', logDateTo);
      const res = await fetch(`${API_URL}/customer-logs/${customerId}?${params.toString()}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCustomerLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch customer logs:', err);
    } finally {
      setLogsLoading(false);
    }
  };

  // Create log entry
  const handleCreateLogEntry = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/customer-logs/${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_type: logEventType,
          note: logNote || null,
          log_timestamp: logTimestamp
        })
      });
      if (!res.ok) throw new Error('Failed to create log entry');
      setShowLogModal(false);
      setLogEventType('visit');
      setLogNote('');
      setLogTimestamp(new Date().toISOString().slice(0, 16));
      success('Log entry created successfully');
      fetchCustomerLogs();
    } catch (err) {
      toastError(err.message);
    }
  };

  // Delete log entry
  const handleDeleteLogEntry = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this log entry?')) return;
    try {
      const res = await fetch(`${API_URL}/customer-logs/${customerId}/${logId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete log entry');
      success('Log entry deleted');
      fetchCustomerLogs();
    } catch (err) {
      toastError(err.message);
    }
  };

  // Format event type for display
  const formatEventType = (type) => {
    const labels = {
      visit: 'Visit',
      phone_call: 'Phone Call',
      sms: 'SMS Message',
      other: 'Other'
    };
    return labels[type] || type;
  };

  // Fetch relationships for this customer
  const fetchRelationships = async () => {
    setRelationshipsLoading(true);
    try {
      const res = await fetch(`${API_URL}/customer-relationships/${customerId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setRelationships(data.relationships);
      }
    } catch (err) {
      console.error('Failed to fetch relationships:', err);
    } finally {
      setRelationshipsLoading(false);
    }
  };

  const handleToggleOptIn = async (field, currentValue) => {
    try {
      const res = await fetch(`${API_URL}/customers/${customerId}/opt-ins`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: !currentValue })
      });

      if (!res.ok) throw new Error('Failed to update opt-in');
      const data = await res.json();
      setCustomer(data.customer);
      success(`Opt-in preference updated successfully`);
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleEditSuccess = (updatedCustomer) => {
    setCustomer(updatedCustomer);
    setShowEditModal(false);
    success('Customer updated successfully');
  };

  const handleModalClose = () => {
    setShowEditModal(false);
  };

  const handleNotesEdit = () => {
    setNotesValue(customer.notes || '');
    setIsEditingNotes(true);
  };

  const handleNotesSave = async () => {
    setSavingNotes(true);
    try {
      const res = await fetch(`${API_URL}/customers/${customerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: notesValue })
      });

      if (!res.ok) throw new Error('Failed to update notes');
      const data = await res.json();
      setCustomer(data.customer);
      setIsEditingNotes(false);
      success('Notes updated successfully');
    } catch (err) {
      toastError(err.message);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleNotesCancel = () => {
    setIsEditingNotes(false);
    setNotesValue('');
  };

  // Store Credits functions
  const fetchStoreCredits = async () => {
    setStoreCreditsLoading(true);
    try {
      const res = await fetch(`${API_URL}/store-credits/${customerId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch store credits');
      const data = await res.json();
      setStoreCredits(data.store_credits);
    } catch (err) {
      toastError(err.message);
    } finally {
      setStoreCreditsLoading(false);
    }
  };

  const fetchStoreCreditsBalance = async () => {
    try {
      const res = await fetch(`${API_URL}/store-credits/balance/${customerId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch balance');
      const data = await res.json();
      setStoreCreditsBalance(data.total_balance);
    } catch (err) {
      console.error('Failed to fetch store credit balance:', err);
    }
  };

  const handleAddStoreCredit = async (e) => {
    e.preventDefault();
    if (!newCreditAmount || parseFloat(newCreditAmount) <= 0) {
      toastError('Amount must be greater than 0');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/store-credits/${customerId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(newCreditAmount),
          notes: newCreditNotes || null,
          applied_at: newCreditDate ? new Date(newCreditDate).toISOString() : null
        })
      });
      if (!res.ok) throw new Error('Failed to add store credit');
      setNewCreditAmount('');
      setNewCreditNotes('');
      setNewCreditDate(new Date().toISOString().split('T')[0]);
      setShowAddCreditForm(false);
      success('Store credit added successfully');
      fetchStoreCredits();
      fetchStoreCreditsBalance();
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleEditStoreCredit = (credit) => {
    setEditingCredit(credit);
    setNewCreditAmount(parseFloat(credit.amount).toFixed(2));
    setNewCreditNotes(credit.notes || '');
    setNewCreditDate(credit.applied_at ? credit.applied_at.split('T')[0] : new Date().toISOString().split('T')[0]);
    setShowAddCreditForm(true);
  };

  const handleUpdateStoreCredit = async (e) => {
    e.preventDefault();
    if (!newCreditAmount || parseFloat(newCreditAmount) <= 0) {
      toastError('Amount must be greater than 0');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/store-credits/${customerId}/${editingCredit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(newCreditAmount),
          notes: newCreditNotes || null,
          applied_at: newCreditDate ? new Date(newCreditDate).toISOString() : null
        })
      });
      if (!res.ok) throw new Error('Failed to update store credit');
      setEditingCredit(null);
      setNewCreditAmount('');
      setNewCreditNotes('');
      setNewCreditDate(new Date().toISOString().split('T')[0]);
      setShowAddCreditForm(false);
      success('Store credit updated successfully');
      fetchStoreCredits();
      fetchStoreCreditsBalance();
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleDeleteStoreCredit = async (creditId) => {
    if (!window.confirm('Are you sure you want to delete this store credit entry?')) return;
    try {
      const res = await fetch(`${API_URL}/store-credits/${customerId}/${creditId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete store credit');
      success('Store credit deleted successfully');
      fetchStoreCredits();
      fetchStoreCreditsBalance();
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleCancelCreditForm = () => {
    setShowAddCreditForm(false);
    setEditingCredit(null);
    setNewCreditAmount('');
    setNewCreditNotes('');
    setNewCreditDate(new Date().toISOString().split('T')[0]);
  };

  // Repairs functions
  const fetchRepairs = async () => {
    setRepairsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('customer_id', customerId);
      if (repairSearchQuery) params.set('search', repairSearchQuery);
      if (repairFilterStatus !== 'any') params.set('ticket_status', repairFilterStatus);
      if (repairFilterJewelryType !== 'any') params.set('jewelry_type', repairFilterJewelryType);
      if (repairFilterDueType !== 'any') params.set('due_type', repairFilterDueType);
      if (repairFilterShopId) params.set('shop_id', repairFilterShopId);
      if (repairDateFrom) params.set('date_from', repairDateFrom);
      if (repairDateTo) params.set('date_to', repairDateTo);
      const queryString = params.toString();
      const url = `${API_URL}/repairs?${queryString}`;

      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch repairs');
      const data = await res.json();
      setRepairs(data.repairs || []);
    } catch (err) {
      toastError(err.message);
    } finally {
      setRepairsLoading(false);
    }
  };

  const openCreateRepairModal = () => {
    setEditingRepair(null);
    setShowRepairModal(true);
  };

  const openEditRepairModal = (repair) => {
    setEditingRepair(repair);
    setShowRepairModal(true);
  };

  const handleRepairModalSuccess = () => {
    success(editingRepair ? 'Repair updated successfully' : 'Repair created successfully');
    setShowRepairModal(false);
    setEditingRepair(null);
    fetchRepairs();
  };

  const handleRepairModalClose = () => {
    setShowRepairModal(false);
    setEditingRepair(null);
    fetchRepairs();
  };

  // Custom Jobs functions
  const fetchCustomJobs = async () => {
    setCustomJobsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('customer_id', customerId);
      if (customJobSearchQuery) params.set('search', customJobSearchQuery);
      if (customJobFilterStatus !== 'any') params.set('ticket_status', customJobFilterStatus);
      if (customJobFilterJewelryType !== 'any') params.set('jewelry_type', customJobFilterJewelryType);
      if (customJobFilterDueType !== 'any') params.set('due_type', customJobFilterDueType);
      if (customJobFilterShopId) params.set('shop_id', customJobFilterShopId);
      if (customJobDateFrom) params.set('date_from', customJobDateFrom);
      if (customJobDateTo) params.set('date_to', customJobDateTo);
      const queryString = params.toString();
      const url = `${API_URL}/custom-jobs?${queryString}`;

      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch custom jobs');
      const data = await res.json();
      setCustomJobs(data.custom_jobs || []);
    } catch (err) {
      toastError(err.message);
    } finally {
      setCustomJobsLoading(false);
    }
  };

  const openCreateCustomJobModal = () => {
    setEditingCustomJob(null);
    setShowCustomJobModal(true);
  };

  const openEditCustomJobModal = (customJob) => {
    setEditingCustomJob(customJob);
    setShowCustomJobModal(true);
  };

  const handleCustomJobModalSuccess = () => {
    success(editingCustomJob ? 'Custom job updated successfully' : 'Custom job created successfully');
    setShowCustomJobModal(false);
    setEditingCustomJob(null);
    fetchCustomJobs();
  };

  const handleCustomJobModalClose = () => {
    setShowCustomJobModal(false);
    setEditingCustomJob(null);
    fetchCustomJobs();
  };

  const handleDeleteCustomJob = async (id) => {
    if (!window.confirm('Are you sure you want to delete this custom job?')) return;
    try {
      const res = await fetch(`${API_URL}/custom-jobs/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete custom job');
      success('Custom job deleted successfully');
      fetchCustomJobs();
    } catch (err) {
      toastError(err.message);
    }
  };

  // Appraisals functions
  const fetchAppraisals = async () => {
    setAppraisalsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('customer_id', customerId);
      if (appraisalSearchQuery) params.set('search', appraisalSearchQuery);
      const queryString = params.toString();
      const url = `${API_URL}/appraisals?${queryString}`;

      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch appraisals');
      const data = await res.json();
      setAppraisals(data.appraisals || []);
    } catch (err) {
      toastError(err.message);
    } finally {
      setAppraisalsLoading(false);
    }
  };

  const openCreateAppraisalModal = () => {
    setEditingAppraisal(null);
    setShowAppraisalModal(true);
  };

  const openEditAppraisalModal = (appraisal) => {
    setEditingAppraisal(appraisal);
    setShowAppraisalModal(true);
  };

  const handleAppraisalModalSuccess = () => {
    success(editingAppraisal ? 'Appraisal updated successfully' : 'Appraisal created successfully');
    setShowAppraisalModal(false);
    setEditingAppraisal(null);
    fetchAppraisals();
  };

  const handleAppraisalModalClose = () => {
    setShowAppraisalModal(false);
    setEditingAppraisal(null);
    fetchAppraisals();
  };

  const handleDeleteAppraisal = async (id) => {
    if (!window.confirm('Are you sure you want to delete this appraisal?')) return;
    try {
      const res = await fetch(`${API_URL}/appraisals/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete appraisal');
      success('Appraisal deleted successfully');
      fetchAppraisals();
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleGoToTransactionFromRepair = async (transactionId) => {
    try {
      const res = await fetch(`${API_URL}/transactions/${transactionId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch transaction');
      const data = await res.json();
      const transactionWithCustomer = {
        ...data,
        customer: {
          id: data.customer_id,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          mobile_phone: data.mobile_phone || '',
          home_phone: data.home_phone || '',
          work_phone: data.work_phone || '',
          email: data.email || '',
          address_line1: data.address_line1 || '',
          address_line2: data.address_line2 || '',
          city: data.city || '',
          state: data.state || '',
          postal_code: data.postal_code || ''
        }
      };
      setEditingTransaction(transactionWithCustomer);
      setShowTransactionModal(true);
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleDeleteRepair = async (id) => {
    if (!window.confirm('Are you sure you want to delete this repair?')) return;
    try {
      const res = await fetch(`${API_URL}/repairs/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to delete repair');
      success('Repair deleted successfully');
      fetchRepairs();
    } catch (err) {
      toastError(err.message);
    }
  };

  const getRepairStatusBadge = (status) => {
    const statusColors = {
      'new': 'bg-gray-100 text-gray-800',
      'in-progress': 'bg-blue-100 text-blue-800',
      'completed': 'bg-green-100 text-green-800',
      'on-hold': 'bg-yellow-100 text-yellow-800'
    };
    const statusLabels = {
      'new': 'New',
      'in-progress': 'In Progress',
      'completed': 'Completed',
      'on-hold': 'On Hold'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const fetchTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('customer_id', customerId);
      if (searchQuery) params.set('search', searchQuery);
      if (filterLayaway !== 'any') params.set('is_layaway', filterLayaway);
      if (filterClosed !== 'any') params.set('is_closed', filterClosed);
      if (filterBalance !== 'any') params.set('balance_due', filterBalance);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (filterShopId) params.set('shop_id', filterShopId);
      const queryString = params.toString();
      const url = `${API_URL}/transactions?${queryString}`;
      
      const res = await fetch(url, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch transactions');
      const data = await res.json();
      setTransactions(data);
    } catch (err) {
      toastError(err.message);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const openCreateTransactionModal = (customerData = null) => {
    setEditingTransaction(null);
    setShowTransactionModal(true);
  };

  const openEditTransactionModal = (transaction) => {
    setEditingTransaction(transaction);
    setShowTransactionModal(true);
  };

  const handleViewTransaction = async (transactionId) => {
    try {
      const res = await fetch(`${API_URL}/transactions/${transactionId}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch transaction');
      const data = await res.json();
      setEditingTransaction(data);
      setShowTransactionModal(true);
    } catch (err) {
      toastError(err.message);
    }
  };

  const handleTransactionModalSuccess = () => {
    success(editingTransaction ? 'Transaction updated successfully' : 'Transaction created successfully');
    setShowTransactionModal(false);
    setEditingTransaction(null);
    fetchTransactions();
    fetchCustomerSummary();
  };

  const handleTransactionModalClose = () => {
    setShowTransactionModal(false);
    setEditingTransaction(null);
    fetchTransactions();
    fetchCustomerSummary();
  };

  const handleDeleteTransaction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;

    try {
      const res = await fetch(`${API_URL}/transactions/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete transaction');
      }

      success('Transaction deleted successfully');
      fetchTransactions();
    } catch (err) {
      toastError(err.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="px-4 py-1 sm:px-0">
        <p>Loading...</p>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="px-4 py-1 sm:px-0">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error || 'Customer not found'}
        </div>
        <button
          onClick={() => navigate('/customers')}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Back to Customers
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col px-4 py-1 sm:px-0">
      {/* Floating Toast Notifications */}
      <ToastContainer toasts={toasts} />

      {/* Main Content Area - Customer Card */}
      <div className="bg-white shadow rounded-lg flex flex-col flex-1 overflow-hidden">
            {/* Header + Tabs - Fixed at top */}
            <div className="flex-shrink-0 p-6 pb-0">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    {customer.first_name} {customer.last_name}
                  </h1>
                  <p className="text-gray-500 mt-1">
                    Customer Since: {new Date(customer.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                  >
                    Edit Customer
                  </button>
                  <button
                    onClick={() => {
                      setLogEventType('visit');
                      setLogNote('');
                      setLogTimestamp(new Date().toISOString().slice(0, 16));
                      setShowLogModal(true);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md hover:bg-emerald-700"
                  >
                    Create Log Entry
                  </button>
                </div>
              </div>

              {/* Summary Stats Section */}
              <div className="mt-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-medium text-gray-500">Summary</h2>
                  <select
                    value={summaryQuickRange}
                    onChange={handleSummaryQuickRangeChange}
                    className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all-time">All Time</option>
                    <option value="this-year">This Year</option>
                    <option value="last-year">Last Year</option>
                    <option value="this-quarter">This Quarter</option>
                    <option value="last-quarter">Last Quarter</option>
                    <option value="this-month">This Month</option>
                    <option value="last-month">Last Month</option>
                    <option value="this-week">This Week</option>
                    <option value="last-week">Last Week</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
                  {/* Transactions Card */}
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-2 shadow-sm">
                    <p className="text-xs font-medium text-gray-500">Transactions</p>
                    <p className="text-lg font-bold text-indigo-700 mt-0.5">
                      {customerSummary ? customerSummary.totalTransactions : '-'}
                    </p>
                  </div>
                  {/* Store Credits Card */}
                  <div className="bg-teal-50 border border-teal-100 rounded-xl p-2 shadow-sm">
                    <p className="text-xs font-medium text-gray-500">Store Credits</p>
                    <p className="text-lg font-bold text-teal-700 mt-0.5">
                      {formatCurrency(storeCreditsBalance)}
                    </p>
                  </div>
                  {/* Payments Card */}
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-2 shadow-sm">
                    <p className="text-xs font-medium text-gray-500">Payments</p>
                    <p className="text-lg font-bold text-emerald-700 mt-0.5">
                      {customerSummary ? formatCurrency(customerSummary.totalPayments) : '-'}
                    </p>
                  </div>
                  {/* Open Balance Card */}
                  <div className="bg-rose-50 border border-rose-100 rounded-xl p-2 shadow-sm">
                    <p className="text-xs font-medium text-gray-500">Open Balance</p>
                    <p className="text-lg font-bold text-rose-700 mt-0.5">
                      {customerSummary ? formatCurrency(customerSummary.openBalance) : '-'}
                    </p>
                  </div>
                  {/* Repairs Card */}
                  <div className="bg-violet-50 border border-violet-100 rounded-xl p-2 shadow-sm">
                    <p className="text-xs font-medium text-gray-500">Repairs</p>
                    <p className="text-lg font-bold text-violet-700 mt-0.5">
                      {customerSummary ? customerSummary.totalRepairs : '-'}
                    </p>
                  </div>
                  {/* Custom Jobs Card */}
                  <div className="bg-orange-50 border border-orange-100 rounded-xl p-2 shadow-sm">
                    <p className="text-xs font-medium text-gray-500">Custom Jobs</p>
                    <p className="text-lg font-bold text-orange-700 mt-0.5">
                      {customerSummary ? customerSummary.totalCustomJobs : '-'}
                    </p>
                  </div>
                  {/* Appraisals Card */}
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-2 shadow-sm">
                    <p className="text-xs font-medium text-gray-500">Appraisals</p>
                    <p className="text-lg font-bold text-amber-700 mt-0.5">
                      {customerSummary ? customerSummary.totalAppraisals : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="mt-6 border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`${
                      activeTab === 'details'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Details
                  </button>
                  <button
                    onClick={() => setActiveTab('transactions')}
                    className={`${
                      activeTab === 'transactions'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Transactions
                  </button>
                  <button
                    onClick={() => setActiveTab('repairs')}
                    className={`${
                      activeTab === 'repairs'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Repairs
                  </button>
                  <button
                    onClick={() => setActiveTab('custom-jobs')}
                    className={`${
                      activeTab === 'custom-jobs'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Custom Jobs
                  </button>
                  <button
                    onClick={() => setActiveTab('appraisals')}
                    className={`${
                      activeTab === 'appraisals'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Appraisals
                  </button>
                  <button
                    onClick={() => setActiveTab('storeCredits')}
                    className={`${
                      activeTab === 'storeCredits'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Store Credits
                  </button>
                  {customer?.mobile_phone && (
                    <button
                      onClick={() => setActiveTab('messages')}
                      className={`${
                        activeTab === 'messages'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                    >
                      Messages
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('friendsAndFamily')}
                    className={`${
                      activeTab === 'friendsAndFamily'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Linked Accounts
                  </button>
                  <button
                    onClick={() => setActiveTab('log')}
                    className={`${
                      activeTab === 'log'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                  >
                    Log
                  </button>
                </nav>
              </div>
            </div>

            {/* Tab Content - Scrollable */}
            <div className="flex-1 overflow-hidden p-6 pt-1">
              {activeTab === 'details' && (
              <div className="mt-6">
                {/* Customer Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                  {/* Left Column - Phone Numbers */}
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <span className="text-gray-500 w-24 shrink-0">Mobile:</span>
                      <span className="text-gray-900 flex-1">{customer.mobile_phone || '-'}</span>
                      <button
                        onClick={() => handleToggleOptIn('opt_in_text_messages', customer.opt_in_text_messages)}
                        className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                          customer.opt_in_text_messages
                            ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        {customer.opt_in_text_messages ? 'SMS ✓' : 'SMS ✗'}
                      </button>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-500 w-24 shrink-0">Home Phone:</span>
                      <span className="text-gray-900 flex-1">{customer.home_phone || '-'}</span>
                      <button
                        onClick={() => handleToggleOptIn('opt_in_phone_messages', customer.opt_in_phone_messages)}
                        className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                          customer.opt_in_phone_messages
                            ? 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
                        }`}
                      >
                        {customer.opt_in_phone_messages ? 'Call ✓' : 'Call ✗'}
                      </button>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-500 w-24 shrink-0">Work Phone:</span>
                      <span className="text-gray-900 flex-1">{customer.work_phone || '-'}</span>
                    </div>
                  </div>
                  {/* Right Column - Email & Address */}
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <span className="text-gray-500 w-24 shrink-0">Email:</span>
                      <span className="text-gray-900 flex-1">{customer.email || '-'}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="text-gray-500 w-24 shrink-0 pt-0.5">Address:</span>
                      <span className="text-gray-900 flex-1">
                        {customer.address_line1 || '-'}
                        {customer.city && customer.state && `, ${customer.city}, ${customer.state}`}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-700">Notes</h3>
                    {!isEditingNotes && (
                      <button
                        onClick={handleNotesEdit}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {isEditingNotes ? (
                    <div>
                      <textarea
                        value={notesValue}
                        onChange={(e) => setNotesValue(e.target.value)}
                        rows={5}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm p-3"
                        placeholder="Enter notes here..."
                      />
                      <div className="mt-2 flex gap-2 justify-end">
                        <button
                          onClick={handleNotesCancel}
                          disabled={savingNotes}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleNotesSave}
                          disabled={savingNotes}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {savingNotes ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-md p-4 cursor-pointer hover:bg-gray-100 transition-colors" onClick={handleNotesEdit}>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{customer.notes || 'No notes added yet.'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="mt-1">
                <div className="flex flex-col gap-4">
                  {/* Search Bar with Filters and Create */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search transactions..."
                      autoComplete="off"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <button
                      onClick={toggleFilters}
                      className={`px-4 py-2 text-sm font-medium border rounded-md ${
                        showFilters
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {showFilters ? '✕ Clear Filters' : '☰ Filters'}
                    </button>
                    <button
                      onClick={() => openCreateTransactionModal()}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Create
                    </button>
                  </div>

                  {/* Filter Bar */}
                  {showFilters && (
                  <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Shop:</span>
                      <select
                        value={filterShopId}
                        onChange={(e) => setFilterShopId(e.target.value)}
                        className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">All Shops</option>
                        {shops.map((shop) => (
                          <option key={shop.id} value={shop.shop_id}>{shop.shop_id}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Date:</span>
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <span className="text-gray-400 text-xs">to</span>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Quick Range:</span>
                      <select
                        value={quickRange}
                        onChange={handleQuickRangeChange}
                        className="px-3 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select Range</option>
                        <option value="this-week">This Week</option>
                        <option value="last-week">Last Week</option>
                        <option value="this-month">This Month</option>
                        <option value="last-month">Last Month</option>
                        <option value="this-quarter">This Quarter</option>
                        <option value="last-quarter">Last Quarter</option>
                        <option value="this-year">This Year</option>
                        <option value="last-year">Last Year</option>
                        <option value="all-time">All Time</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Layaway:</span>
                      <div className="flex rounded-md shadow-sm">
                        {['any', 'true', 'false'].map(val => (
                          <button
                            key={val}
                            onClick={() => setFilterLayaway(val)}
                            className={`px-3 py-1 text-xs font-medium first:rounded-l-md last:rounded-r-md border ${
                              filterLayaway === val
                                ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            } ${val !== 'any' ? '-ml-px' : ''}`}
                          >
                            {val === 'any' ? 'Any' : val === 'true' ? 'Yes' : 'No'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Closed:</span>
                      <div className="flex rounded-md shadow-sm">
                        {['any', 'true', 'false'].map(val => (
                          <button
                            key={val}
                            onClick={() => setFilterClosed(val)}
                            className={`px-3 py-1 text-xs font-medium first:rounded-l-md last:rounded-r-md border ${
                              filterClosed === val
                                ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            } ${val !== 'any' ? '-ml-px' : ''}`}
                          >
                            {val === 'any' ? 'Any' : val === 'true' ? 'Yes' : 'No'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Balance:</span>
                      <div className="flex rounded-md shadow-sm">
                        {['any', 'paid', 'balance'].map(val => (
                          <button
                            key={val}
                            onClick={() => setFilterBalance(val)}
                            className={`px-3 py-1 text-xs font-medium first:rounded-l-md last:rounded-r-md border ${
                              filterBalance === val
                                ? 'bg-indigo-600 text-white border-indigo-600 z-10'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            } ${val !== 'any' ? '-ml-px' : ''}`}
                          >
                            {val === 'any' ? 'Any' : val === 'paid' ? 'Paid' : 'Balance Due'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Transactions Table */}
                  <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                    {transactionsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading transactions...</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Date</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Shop</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Details</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Total</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Balance</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Status</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {transactions.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                                    {searchQuery ? 'No transactions found matching your search' : 'No transactions found for this customer'}
                                  </td>
                                </tr>
                              ) : (
                                transactions.map((transaction) => {
                                  const firstLineItemTitle = transaction.first_line_item_title;
                                  const firstLineItemDesc = transaction.first_line_item_description;
                                  const firstLineItemPrice = transaction.first_line_item_unit_price;
                                  const detailsText = firstLineItemTitle
                                    ? `${firstLineItemTitle} - ${formatCurrency(firstLineItemPrice)}`
                                    : firstLineItemDesc
                                      ? `${firstLineItemDesc} - ${formatCurrency(firstLineItemPrice)}`
                                      : 'Transaction';
                                  
                                  return (
                                    <tr
                                      key={transaction.id}
                                      className="hover:bg-gray-50 cursor-pointer"
                                      onClick={() => openEditTransactionModal(transaction)}
                                    >
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center gap-2">
                                          {transaction.is_layaway && (
                                            <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-indigo-700 bg-indigo-100 rounded" title="Layaway">L</span>
                                          )}
                                          <span>{new Date(transaction.created_at).toLocaleDateString()}</span>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4">
                                        {transaction.shop_id ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                            {transaction.shop_id}
                                          </span>
                                        ) : (
                                          <span className="text-xs text-gray-400">-</span>
                                        )}
                                      </td>
                                      <td className="px-6 py-4 text-sm text-gray-900">
                                        <span className="truncate block max-w-xs" title={detailsText}>
                                          {detailsText.length > 50 ? detailsText.substring(0, 47) + '...' : detailsText}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                        {formatCurrency(transaction.total_amount)}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        {(() => {
                                          const total = parseFloat(transaction.total_amount) || 0;
                                          const paid = parseFloat(transaction.payments_sum) || 0;
                                          const balance = total - paid;
                                          return (
                                            <span className={balance <= 0 ? 'text-green-600' : 'text-red-600'}>
                                              {formatCurrency(balance)}
                                            </span>
                                          );
                                        })()}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="flex items-center gap-2">
                                          {getStatusBadge(transaction.status)}
                                          {transaction.is_closed && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                              Closed
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                                        <button
                                          onClick={() => window.open(`${API_URL}/transactions/${transaction.id}/pdf`, '_blank')}
                                          className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-14V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                          Print
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Repairs Tab */}
            {activeTab === 'repairs' && (
              <div className="mt-1">
                <div className="flex flex-col gap-4">
                  {/* Search Bar with Filters and Create */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search repairs..."
                      autoComplete="off"
                      value={repairSearchQuery}
                      onChange={(e) => setRepairSearchQuery(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <button
                      onClick={repairToggleFilters}
                      className={`px-4 py-2 text-sm font-medium border rounded-md ${
                        repairShowFilters
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {repairShowFilters ? '\u2715 Clear Filters' : '\u2630 Filters'}
                    </button>
                    <button
                      onClick={openCreateRepairModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Create
                    </button>
                  </div>

                  {/* Filter Bar */}
                  {repairShowFilters && (
                  <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                    {/* Shop */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Shop:</span>
                      <select
                        value={repairFilterShopId}
                        onChange={(e) => setRepairFilterShopId(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="">Any</option>
                        {shops.map(shop => (
                          <option key={shop.shop_id} value={shop.shop_id}>{shop.shop_id}</option>
                        ))}
                      </select>
                    </div>
                    {/* Date Range */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Date:</span>
                      <input
                        type="date"
                        value={repairDateFrom}
                        onChange={(e) => setRepairDateFrom(e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <span className="text-gray-400 text-xs">to</span>
                      <input
                        type="date"
                        value={repairDateTo}
                        onChange={(e) => setRepairDateTo(e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    {/* Quick Range */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Quick Range:</span>
                      <select
                        value={repairQuickRange}
                        onChange={(e) => {
                          const range = e.target.value;
                          setRepairQuickRange(range);
                          const rangeHandlers = {
                            'this-week': () => { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); setRepairDateFrom(formatDate(s)); setRepairDateTo(formatDate(today)); },
                            'last-week': () => { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); const l = new Date(s); l.setDate(s.getDate() - 7); setRepairDateFrom(formatDate(l)); setRepairDateTo(formatDate(s)); },
                            'this-month': () => { const m = new Date(today.getFullYear(), today.getMonth(), 1); setRepairDateFrom(formatDate(m)); setRepairDateTo(formatDate(today)); },
                            'last-month': () => { const m = new Date(today.getFullYear(), today.getMonth(), 1); const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1); setRepairDateFrom(formatDate(lm)); setRepairDateTo(formatDate(m)); },
                            'this-quarter': () => { const q = Math.floor(today.getMonth() / 3); const qs = new Date(today.getFullYear(), q * 3, 1); setRepairDateFrom(formatDate(qs)); setRepairDateTo(formatDate(today)); },
                            'last-quarter': () => { const cq = Math.floor(today.getMonth() / 3); const tq = new Date(today.getFullYear(), cq * 3, 1); const lq = new Date(today.getFullYear(), (cq - 1) * 3, 1); setRepairDateFrom(formatDate(lq)); setRepairDateTo(formatDate(tq)); },
                            'this-year': () => { const y = new Date(today.getFullYear(), 0, 1); setRepairDateFrom(formatDate(y)); setRepairDateTo(formatDate(today)); },
                            'last-year': () => { const ly = today.getFullYear() - 1; const lys = new Date(ly, 0, 1); const tys = new Date(today.getFullYear(), 0, 1); setRepairDateFrom(formatDate(lys)); setRepairDateTo(formatDate(tys)); },
                            'all-time': () => { setRepairDateFrom('2000-01-01'); setRepairDateTo(formatDate(today)); },
                          };
                          if (rangeHandlers[range]) rangeHandlers[range]();
                          else { setRepairDateFrom(''); setRepairDateTo(''); }
                        }}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select Range</option>
                        <option value="this-week">This Week</option>
                        <option value="last-week">Last Week</option>
                        <option value="this-month">This Month</option>
                        <option value="last-month">Last Month</option>
                        <option value="this-quarter">This Quarter</option>
                        <option value="last-quarter">Last Quarter</option>
                        <option value="this-year">This Year</option>
                        <option value="last-year">Last Year</option>
                        <option value="all-time">All Time</option>
                      </select>
                    </div>
                    {/* Status */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Status:</span>
                      <select
                        value={repairFilterStatus}
                        onChange={(e) => setRepairFilterStatus(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="any">Any</option>
                        <option value="new">New</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="on-hold">On Hold</option>
                      </select>
                    </div>
                    {/* Type */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Type:</span>
                      <select
                        value={repairFilterJewelryType}
                        onChange={(e) => setRepairFilterJewelryType(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="any">Any</option>
                        <option value="Ring">Ring</option>
                        <option value="Earrings">Earrings</option>
                        <option value="Necklace">Necklace</option>
                        <option value="Bracelet">Bracelet</option>
                        <option value="Watch">Watch</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    {/* Due */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Due:</span>
                      <select
                        value={repairFilterDueType}
                        onChange={(e) => setRepairFilterDueType(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="any">Any</option>
                        <option value="date">By Date</option>
                        <option value="will-call">Will Call</option>
                      </select>
                    </div>
                  </div>
                  )}

                  {/* Repairs Table */}
                  <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                    {repairsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading repairs...</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Shop</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Created / Due</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Title / Description</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Type / Status</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Price</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Transaction</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {repairs.length === 0 ? (
                                <tr>
                                  <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">
                                    No repairs found for this customer
                                  </td>
                                </tr>
                              ) : (
                                repairs.map((repair) => (
                                  <tr
                                    key={repair.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => openEditRepairModal(repair)}
                                  >
                                    <td className="px-6 py-4">
                                      {repair.shop_id ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                          {repair.shop_id}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                      <div>{repair.created_at ? new Date(repair.created_at).toLocaleDateString() : '-'}</div>
                                      <div className="font-semibold">
                                        {repair.due_type === 'will-call' ? (
                                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Will Call</span>
                                        ) : (
                                          repair.due_date ? new Date(repair.due_date).toLocaleDateString() : '-'
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm font-medium text-gray-900">{repair.title}</div>
                                      {repair.description && (
                                        <div className="text-xs text-gray-400 truncate max-w-xs">
                                          {repair.description.substring(0, 50)}
                                          {repair.description.length > 50 ? '...' : ''}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-gray-600">{repair.jewelry_type || '-'}</div>
                                      {getRepairStatusBadge(repair.ticket_status)}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">{formatCurrency(repair.price)}</td>
                                    <td className="px-6 py-4 text-sm">
                                      {repair.transaction_id ? (
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                          #{repair.transaction_id}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        onClick={() => window.open(`${API_URL}/repairs/${repair.id}/pdf`, '_blank')}
                                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-14V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                        Print
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Custom Jobs Tab */}
            {activeTab === 'custom-jobs' && (
              <div className="mt-1">
                <div className="flex flex-col gap-4">
                  {/* Search Bar with Filters and Create */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search custom jobs..."
                      autoComplete="off"
                      value={customJobSearchQuery}
                      onChange={(e) => setCustomJobSearchQuery(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <button
                      onClick={customJobToggleFilters}
                      className={`px-4 py-2 text-sm font-medium border rounded-md ${
                        customJobShowFilters
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {customJobShowFilters ? '\u2715 Clear Filters' : '\u2630 Filters'}
                    </button>
                    <button
                      onClick={openCreateCustomJobModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Create
                    </button>
                  </div>

                  {/* Filter Bar */}
                  {customJobShowFilters && (
                  <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-gray-200">
                    {/* Shop */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Shop:</span>
                      <select
                        value={customJobFilterShopId}
                        onChange={(e) => setCustomJobFilterShopId(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="">Any</option>
                        {shops.map(shop => (
                          <option key={shop.shop_id} value={shop.shop_id}>{shop.shop_id}</option>
                        ))}
                      </select>
                    </div>
                    {/* Date Range */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Date:</span>
                      <input
                        type="date"
                        value={customJobDateFrom}
                        onChange={(e) => setCustomJobDateFrom(e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <span className="text-gray-400 text-xs">to</span>
                      <input
                        type="date"
                        value={customJobDateTo}
                        onChange={(e) => setCustomJobDateTo(e.target.value)}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    {/* Quick Range */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Quick Range:</span>
                      <select
                        value={customJobQuickRange}
                        onChange={(e) => {
                          const range = e.target.value;
                          setCustomJobQuickRange(range);
                          const rangeHandlers = {
                            'this-week': () => { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); setCustomJobDateFrom(formatDate(s)); setCustomJobDateTo(formatDate(today)); },
                            'last-week': () => { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); const l = new Date(s); l.setDate(s.getDate() - 7); setCustomJobDateFrom(formatDate(l)); setCustomJobDateTo(formatDate(s)); },
                            'this-month': () => { const m = new Date(today.getFullYear(), today.getMonth(), 1); setCustomJobDateFrom(formatDate(m)); setCustomJobDateTo(formatDate(today)); },
                            'last-month': () => { const m = new Date(today.getFullYear(), today.getMonth(), 1); const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1); setCustomJobDateFrom(formatDate(lm)); setCustomJobDateTo(formatDate(m)); },
                            'this-quarter': () => { const q = Math.floor(today.getMonth() / 3); const qs = new Date(today.getFullYear(), q * 3, 1); setCustomJobDateFrom(formatDate(qs)); setCustomJobDateTo(formatDate(today)); },
                            'last-quarter': () => { const cq = Math.floor(today.getMonth() / 3); const tq = new Date(today.getFullYear(), cq * 3, 1); const lq = new Date(today.getFullYear(), (cq - 1) * 3, 1); setCustomJobDateFrom(formatDate(lq)); setCustomJobDateTo(formatDate(tq)); },
                            'this-year': () => { const y = new Date(today.getFullYear(), 0, 1); setCustomJobDateFrom(formatDate(y)); setCustomJobDateTo(formatDate(today)); },
                            'last-year': () => { const ly = today.getFullYear() - 1; const lys = new Date(ly, 0, 1); const tys = new Date(today.getFullYear(), 0, 1); setCustomJobDateFrom(formatDate(lys)); setCustomJobDateTo(formatDate(tys)); },
                            'all-time': () => { setCustomJobDateFrom('2000-01-01'); setCustomJobDateTo(formatDate(today)); },
                          };
                          if (rangeHandlers[range]) rangeHandlers[range]();
                          else { setCustomJobDateFrom(''); setCustomJobDateTo(''); }
                        }}
                        className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select Range</option>
                        <option value="this-week">This Week</option>
                        <option value="last-week">Last Week</option>
                        <option value="this-month">This Month</option>
                        <option value="last-month">Last Month</option>
                        <option value="this-quarter">This Quarter</option>
                        <option value="last-quarter">Last Quarter</option>
                        <option value="this-year">This Year</option>
                        <option value="last-year">Last Year</option>
                        <option value="all-time">All Time</option>
                      </select>
                    </div>
                    {/* Status */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Status:</span>
                      <select
                        value={customJobFilterStatus}
                        onChange={(e) => setCustomJobFilterStatus(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="any">Any</option>
                        <option value="new">New</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="on-hold">On Hold</option>
                      </select>
                    </div>
                    {/* Type */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Type:</span>
                      <select
                        value={customJobFilterJewelryType}
                        onChange={(e) => setCustomJobFilterJewelryType(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="any">Any</option>
                        <option value="Ring">Ring</option>
                        <option value="Earrings">Earrings</option>
                        <option value="Necklace">Necklace</option>
                        <option value="Bracelet">Bracelet</option>
                        <option value="Watch">Watch</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    {/* Due */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-600">Due:</span>
                      <select
                        value={customJobFilterDueType}
                        onChange={(e) => setCustomJobFilterDueType(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm"
                      >
                        <option value="any">Any</option>
                        <option value="date">By Date</option>
                        <option value="will-call">Will Call</option>
                      </select>
                    </div>
                  </div>
                  )}

                  {/* Custom Jobs Table */}
                  <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                    {customJobsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading custom jobs...</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Shop</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Created / Due</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Title / Description</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Type / Status</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Price</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Transaction</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {customJobs.length === 0 ? (
                                <tr>
                                  <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">
                                    No custom jobs found for this customer
                                  </td>
                                </tr>
                              ) : (
                                customJobs.map((customJob) => (
                                  <tr
                                    key={customJob.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => openEditCustomJobModal(customJob)}
                                  >
                                    <td className="px-6 py-4">
                                      {customJob.shop_id ? (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                          {customJob.shop_id}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                      <div>{customJob.created_at ? new Date(customJob.created_at).toLocaleDateString() : '-'}</div>
                                      <div className="font-semibold">
                                        {customJob.due_type === 'will-call' ? (
                                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Will Call</span>
                                        ) : (
                                          customJob.due_date ? new Date(customJob.due_date).toLocaleDateString() : '-'
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm font-medium text-gray-900">{customJob.title}</div>
                                      {customJob.description && (
                                        <div className="text-xs text-gray-400 truncate max-w-xs">
                                          {customJob.description.substring(0, 50)}
                                          {customJob.description.length > 50 ? '...' : ''}
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-gray-600">{customJob.jewelry_type || '-'}</div>
                                      {getRepairStatusBadge(customJob.ticket_status)}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">{formatCurrency(customJob.price)}</td>
                                    <td className="px-6 py-4 text-sm">
                                      {customJob.transaction_id ? (
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                          #{customJob.transaction_id}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        onClick={() => window.open(`${API_URL}/custom-jobs/${customJob.id}/pdf?shop=ejr`, '_blank')}
                                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-14V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                        Print
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Appraisals Tab */}
            {activeTab === 'appraisals' && (
              <div className="mt-1">
                <div className="flex flex-col gap-4">
                  {/* Search Bar with Create */}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Search appraisals..."
                      autoComplete="off"
                      value={appraisalSearchQuery}
                      onChange={(e) => setAppraisalSearchQuery(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <button
                      onClick={openCreateAppraisalModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      Create
                    </button>
                  </div>

                  {/* Appraisals Table */}
                  <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                    {appraisalsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Loading appraisals...</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Shop</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Date</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Description</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Value</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Transaction</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {appraisals.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                                    No appraisals found for this customer
                                  </td>
                                </tr>
                              ) : (
                                appraisals.map((appraisal) => (
                                  <tr
                                    key={appraisal.id}
                                    className="hover:bg-gray-50 cursor-pointer"
                                    onClick={() => openEditAppraisalModal(appraisal)}
                                  >
                                    <td className="px-6 py-4">
                                      {appraisal.shop_id && (
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                          {appraisal.shop_id}
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                      {appraisal.prepared_date ? new Date(appraisal.prepared_date).toLocaleDateString() : '-'}
                                    </td>
                                    <td className="px-6 py-4">
                                      <div className="text-sm text-gray-900">
                                        {appraisal.description ? (
                                          <>
                                            {appraisal.description.substring(0, 60)}
                                            {appraisal.description.length > 60 ? '...' : ''}
                                          </>
                                        ) : (
                                          <span className="text-gray-400">-</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">{formatCurrency(appraisal.value)}</td>
                                    <td className="px-6 py-4 text-sm">
                                      {appraisal.transaction_id ? (
                                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">#{appraisal.transaction_id}</span>
                                      ) : <span className="text-xs text-gray-400">-</span>}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        onClick={() => window.open(`${API_URL}/appraisals/${appraisal.id}/pdf`, '_blank')}
                                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-1"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-14V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                        Print
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Store Credits Tab */}
            {activeTab === 'storeCredits' && (
              <div className="mt-1">
                {/* Balance Summary */}
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-green-800">Available Store Credit Balance</h3>
                      <p className="text-2xl font-bold text-green-900">{formatCurrency(storeCreditsBalance)}</p>
                    </div>
                    <button
                      onClick={() => {
                        if (editingCredit) {
                          setEditingCredit(null);
                        }
                        setNewCreditAmount('');
                        setNewCreditNotes('');
                        setNewCreditDate(new Date().toISOString().split('T')[0]);
                        setShowAddCreditForm(!showAddCreditForm);
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
                    >
                      {editingCredit ? 'Cancel Edit' : 'Add Store Credit'}
                    </button>
                  </div>
                </div>

                {/* Add/Edit Store Credit Form */}
                {showAddCreditForm && (
                  <div className="mb-4 p-4 bg-white border border-gray-200 rounded-md">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      {editingCredit ? 'Edit Store Credit Entry' : 'Add New Store Credit'}
                    </h3>
                    <form onSubmit={editingCredit ? handleUpdateStoreCredit : handleAddStoreCredit}>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                          <input
                            type="number"
                            autoComplete="off"
                            step="0.01"
                            min="0.01"
                            required
                            value={newCreditAmount}
                            onChange={(e) => setNewCreditAmount(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Date Applied</label>
                          <input
                            type="date"
                            autoComplete="off"
                            required
                            value={newCreditDate}
                            onChange={(e) => setNewCreditDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                          <input
                            type="text"
                            autoComplete="off"
                            value={newCreditNotes}
                            onChange={(e) => setNewCreditNotes(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            placeholder="Optional notes..."
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={handleCancelCreditForm}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                        >
                          {editingCredit ? 'Update' : 'Add'} Store Credit
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Store Credits Table */}
                <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                  {storeCreditsLoading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Loading store credits...</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Notes</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Type</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {storeCredits.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                                  No store credit entries found. Click "Add Store Credit" to create one.
                                </td>
                              </tr>
                            ) : (
                              storeCredits.map((credit) => {
                                const isPositive = parseFloat(credit.amount) > 0;
                                return (
                                  <tr key={credit.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {new Date(credit.applied_at).toLocaleDateString()}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                      {isPositive ? '+' : ''}{formatCurrency(Math.abs(parseFloat(credit.amount)))}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={credit.notes || ''}>
                                      {credit.notes || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                      {isPositive ? (
                                        <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">Credit</span>
                                      ) : (
                                        <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800">Used</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                      {credit.transaction_id && (
                                        <button
                                          onClick={() => handleViewTransaction(credit.transaction_id)}
                                          className="text-blue-600 hover:text-blue-900 mr-3"
                                          title={`View Transaction #${credit.transaction_id}`}
                                        >
                                          View Tx #{credit.transaction_id}
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleEditStoreCredit(credit)}
                                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() => handleDeleteStoreCredit(credit.id)}
                                        className="text-red-600 hover:text-red-900"
                                      >
                                        Delete
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <div className="h-full flex flex-col -m-6">
                {messagesLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-gray-500">Loading messages...</div>
                  </div>
                ) : (
                  <MessageChat
                    conversationSid={customerConversation?.conversation_sid || null}
                    phoneNumber={customer?.mobile_phone || ''}
                    customerName={`${customer?.first_name || ''} ${customer?.last_name || ''}`.trim()}
                    customerId={customer?.id}
                    onConversationStarted={handleConversationStarted}
                  />
                )}
              </div>
            )}

            {/* Friends & Family Tab */}
            {activeTab === 'friendsAndFamily' && (
              <div className="mt-1">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Relationships</h3>
                  <button
                    onClick={() => setShowAddRelationshipModal(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                  >
                    + Add Friend/Family
                  </button>
                </div>

                {relationshipsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading relationships...</div>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                    <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Customer Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Relationship</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {relationships.length === 0 ? (
                            <tr>
                              <td colSpan="3" className="px-6 py-8 text-center text-sm text-gray-500">
                                No relationships yet. Click "Add Friend/Family" to get started.
                              </td>
                            </tr>
                          ) : (
                            relationships.map(rel => {
                              let relationshipLabel;
                              if (rel.direction === 'outgoing') {
                                relationshipLabel = rel.relationship_to_me;
                              } else {
                                relationshipLabel = rel.relationship_to_them;
                              }

                              const formatLabel = (value) => {
                                return value
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, l => l.toUpperCase());
                              };

                              return (
                                <tr key={rel.id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <button
                                      onClick={() => navigate(`/customers/${rel.related_customer.id}/dashboard`)}
                                      className="text-indigo-600 hover:text-indigo-900 underline"
                                    >
                                      {rel.related_customer.first_name} {rel.related_customer.last_name}
                                    </button>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    <span className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800">
                                      {formatLabel(relationshipLabel)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                      onClick={() => {
                                        setEditingRelationship(rel);
                                        setShowAddRelationshipModal(true);
                                      }}
                                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if (window.confirm(`Remove the relationship with ${rel.related_customer.first_name} ${rel.related_customer.last_name}?`)) {
                                          try {
                                            const res = await fetch(`${API_URL}/customer-relationships/${rel.id}`, {
                                              method: 'DELETE',
                                              credentials: 'include'
                                            });
                                            if (res.ok) {
                                              setRelationships(prev => prev.filter(r => r.id !== rel.id));
                                              success('Relationship removed');
                                            } else {
                                              const data = await res.json();
                                              toastError(data.error || 'Failed to remove relationship');
                                            }
                                          } catch (err) {
                                            toastError('Failed to remove relationship');
                                          }
                                        }
                                      }}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              )}
  
              {/* Log Tab */}
              {activeTab === 'log' && (
                <div className="mt-1">
                  {/* Log Filters */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <select
                        value={logQuickRange}
                        onChange={handleLogQuickRangeChange}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Quick Range</option>
                        <option value="this-week">This Week</option>
                        <option value="this-month">This Month</option>
                        <option value="this-quarter">This Quarter</option>
                        <option value="this-year">This Year</option>
                        <option value="all-time">All Time</option>
                      </select>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">From:</label>
                        <input
                          type="date"
                          value={logDateFrom}
                          onChange={(e) => {
                            setLogDateFrom(e.target.value);
                            setLogQuickRange('');
                          }}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600">To:</label>
                        <input
                          type="date"
                          value={logDateTo}
                          onChange={(e) => {
                            setLogDateTo(e.target.value);
                            setLogQuickRange('');
                          }}
                          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      {(logDateFrom || logDateTo || logQuickRange) && (
                        <button
                          onClick={() => {
                            setLogDateFrom('');
                            setLogDateTo('');
                            setLogQuickRange('');
                          }}
                          className="text-sm text-gray-500 hover:text-gray-700 underline"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
  
                  {/* Log Entries Table */}
                  {logsLoading ? (
                    <div className="text-center py-8 text-gray-500">Loading logs...</div>
                  ) : customerLogs.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No log entries found.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {customerLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(log.log_timestamp).toLocaleDateString()} {new Date(log.log_timestamp).toLocaleTimeString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                                  {formatEventType(log.event_type)}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                {log.note || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {log.created_by_name || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                <button
                                  onClick={() => handleDeleteLogEntry(log.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

      {/* Edit Customer Modal */}
      <CustomerModal
        showModal={showEditModal}
        onClose={handleModalClose}
        onSuccess={handleEditSuccess}
        editingCustomer={customer}
        initialData={customer ? {
          first_name: customer.first_name || '',
          last_name: customer.last_name || '',
          mobile_phone: customer.mobile_phone || '',
          home_phone: customer.home_phone || '',
          work_phone: customer.work_phone || '',
          email: customer.email || '',
          opt_in_text_messages: customer.opt_in_text_messages || false,
          opt_in_phone_messages: customer.opt_in_phone_messages || false,
          address_line1: customer.address_line1 || '',
          address_line2: customer.address_line2 || '',
          city: customer.city || '',
          state: customer.state || '',
          postal_code: customer.postal_code || '',
          notes: customer.notes || ''
        } : {}}
      />

      {/* Transaction Modal */}
      <TransactionModal
        isOpen={showTransactionModal}
        onClose={handleTransactionModalClose}
        customerId={customerId}
        customer={customer}
        editingTransaction={editingTransaction}
        onSuccess={handleTransactionModalSuccess}
      />

      {/* Repair Modal */}
      <RepairModal
        isOpen={showRepairModal}
        onClose={handleRepairModalClose}
        repair={editingRepair}
        customer={customer}
        onSuccess={handleRepairModalSuccess}
        onDelete={() => handleDeleteRepair(editingRepair?.id)}
        onGoToTransaction={handleGoToTransactionFromRepair}
      />

      {/* Appraisal Modal */}
      <AppraisalModal
        isOpen={showAppraisalModal}
        onClose={handleAppraisalModalClose}
        appraisal={editingAppraisal}
        customer={customer}
        onSuccess={handleAppraisalModalSuccess}
        onDelete={() => handleDeleteAppraisal(editingAppraisal?.id)}
        onGoToTransaction={handleGoToTransactionFromRepair}
      />

      {/* Custom Job Modal */}
      <CustomJobModal
        isOpen={showCustomJobModal}
        onClose={handleCustomJobModalClose}
        customJob={editingCustomJob}
        customer={customer}
        onSuccess={handleCustomJobModalSuccess}
        onDelete={() => handleDeleteCustomJob(editingCustomJob?.id)}
      />

      {/* Add Relationship Modal */}
      <AddRelationshipModal
        showModal={showAddRelationshipModal}
        onClose={() => {
          setShowAddRelationshipModal(false);
          setEditingRelationship(null);
        }}
        currentCustomerId={parseInt(customerId)}
        currentCustomerName={customer ? `${customer.first_name} ${customer.last_name}`.trim() : ''}
        editingRelationship={editingRelationship}
        onSuccess={() => {
          fetchRelationships();
          setShowAddRelationshipModal(false);
          setEditingRelationship(null);
        }}
      />

      {/* Create Log Entry Modal */}
      {showLogModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Create Log Entry</h2>
              <button
                onClick={() => setShowLogModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleCreateLogEntry}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
                  <select
                    value={logEventType}
                    onChange={(e) => setLogEventType(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="visit">Visit</option>
                    <option value="phone_call">Phone Call</option>
                    <option value="sms">SMS Message</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
                  <input
                    type="datetime-local"
                    value={logTimestamp}
                    onChange={(e) => setLogTimestamp(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <textarea
                    value={logNote}
                    onChange={(e) => setLogNote(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter a note (optional)..."
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
                >
                  Create Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
